import { useState, useRef, useEffect } from "react"
import { Send, MessageCircle, Bot, User, Mic, ChevronRight, X, Trash2, RefreshCw } from "lucide-react"
import { ChatbotIcon } from "./icons/ChatbotIcon" // Assuming this is a custom icon component
import axiosInstance from "../services/BaseUrl"

const ChatBot = ({ propertyId: initialPropertyId }) => {
  const [messages, setMessages] = useState([])
  const [inputValue, setInputValue] = useState("")
  const [isTyping, setIsTyping] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [isLoadingGreeting, setIsLoadingGreeting] = useState(false)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [propertyId, setPropertyId] = useState(initialPropertyId || null)
  const [recommendations, setRecommendations] = useState([])
  const messagesEndRef = useRef(null)
  const chatContainerRef = useRef(null)
  const inputRef = useRef(null)
  const recognitionRef = useRef(null)

  const API_URL = "/chatbot/"
  const HISTORY_URL = "chatbot/history/"

  // Configure axios to include credentials (cookies) in requests
  useEffect(() => {
    axiosInstance.defaults.withCredentials = true
  }, [])

  // Handle property ID changes
  useEffect(() => {
    if (initialPropertyId && initialPropertyId !== propertyId) {
      setPropertyId(initialPropertyId)
      setMessages([])
      fetchInitialGreeting()
    }
  }, [initialPropertyId])

  // Enhanced responsive detection
  useEffect(() => {
    const checkDeviceType = () => {
      const width = window.innerWidth
      const height = window.innerHeight

      setIsMobile(width <= 640) // sm breakpoint
      setIsTablet(width > 640 && width <= 1024) // between sm and lg
    }

    checkDeviceType()
    window.addEventListener("resize", checkDeviceType)

    const timer = setTimeout(() => {
      setIsVisible(true)
    }, 60000) // 60-second delay

    return () => {
      window.removeEventListener("resize", checkDeviceType)
      clearTimeout(timer)
    }
  }, [])

  // Handle exit intent for desktop and mobile
  useEffect(() => {
    let hasTriggeredExitIntent = false
    let lastScrollPosition = 0
    let lastScrollTime = Date.now()
    let lastMouseY = null
    let lastMouseTime = Date.now()

    const handleExitIntentDesktop = (event) => {
      if (hasTriggeredExitIntent || isVisible || isMobile) return

      const currentMouseY = event.clientY
      const currentTime = Date.now()
      const windowWidth = window.innerWidth

      const isNearTop = currentMouseY <= 50
      const isAddressBarRegion =
        currentMouseY <= 50 && event.clientX >= windowWidth * 0.2 && event.clientX <= windowWidth * 0.8
      const isCloseButtonRegion = currentMouseY <= 50 && event.clientX >= windowWidth * 0.9

      let isMovingUpward = false
      if (lastMouseY !== null) {
        const deltaY = lastMouseY - currentMouseY
        const deltaTime = currentTime - lastMouseTime
        const mouseSpeedY = deltaY / deltaTime
        isMovingUpward = mouseSpeedY > 0
      }

      if ((isAddressBarRegion || isCloseButtonRegion) && isNearTop && isMovingUpward) {
        setIsVisible(true)
        setIsMinimized(false)
        hasTriggeredExitIntent = true
      }

      lastMouseY = currentMouseY
      lastMouseTime = currentTime
    }

    const handleExitIntentMobile = () => {
      if (hasTriggeredExitIntent || isVisible || !isMobile) return
      const currentScrollPosition = window.scrollY
      const currentTime = Date.now()
      const scrollSpeed = (lastScrollPosition - currentScrollPosition) / (currentTime - lastScrollTime)

      if (scrollSpeed > 0.5) {
        setIsVisible(true)
        setIsMinimized(false)
        hasTriggeredExitIntent = true
      }

      lastScrollPosition = currentScrollPosition
      lastScrollTime = currentTime
    }

    if (!isMobile) {
      document.addEventListener("mousemove", handleExitIntentDesktop)
    } else {
      window.addEventListener("scroll", handleExitIntentMobile)
    }

    return () => {
      if (!isMobile) {
        document.removeEventListener("mousemove", handleExitIntentDesktop)
      } else {
        window.removeEventListener("scroll", handleExitIntentMobile)
      }
    }
  }, [isMobile, isVisible])

  // Initialize speech recognition
  useEffect(() => {
    if ("SpeechRecognition" in window || "webkitSpeechRecognition" in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      recognitionRef.current = new SpeechRecognition()
      recognitionRef.current.continuous = false
      recognitionRef.current.interimResults = true
      recognitionRef.current.lang = "en-US"

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map((result) => result[0].transcript)
          .join("")
        setInputValue(transcript)
      }

      recognitionRef.current.onerror = (event) => {
        console.error("Speech recognition error:", event.error)
        setIsRecording(false)
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            text: "Speech recognition failed. Please try again or type your message.",
            sender: "bot",
            timestamp: new Date(),
          },
        ])
      }

      recognitionRef.current.onend = () => {
        setIsRecording(false)
        if (inputValue.trim()) {
          handleSendMessage()
        }
      }
    }
  }, [inputValue])

  // Load chat history when component becomes visible
  useEffect(() => {
    const initializeSessionAndFetchHistory = async () => {
      if (!propertyId) {
        fetchInitialGreeting()
        return
      }

      try {
        await axiosInstance.post(API_URL, {
          property_id: propertyId,
          intent: "initialize_session",
          message: "",
        })

        setIsLoadingHistory(true)
        const response = await axiosInstance.get(HISTORY_URL, {
          params: { property_id: propertyId },
        })

        if (response.data.success) {
          const conversationMessages = response.data.conversations
            .flatMap((conv) => [
              conv.user_message && {
                id: `${conv.id}-user`,
                text: conv.user_message,
                sender: "user",
                timestamp: new Date(conv.timestamp),
                action_suggestions: [],
              },
              conv.bot_response && {
                id: `${conv.id}-bot`,
                text: conv.bot_response,
                sender: "bot",
                timestamp: new Date(conv.timestamp),
                action_suggestions: conv.action_suggestions || [],
              },
            ])
            .filter(Boolean)

          setMessages(conversationMessages)

          if (conversationMessages.length === 0) {
            fetchInitialGreeting()
          }
        }
      } catch (error) {
        console.error("Error initializing session or fetching chat history:", error)
        fetchInitialGreeting()
      } finally {
        setIsLoadingHistory(false)
      }
    }

    if (isVisible) {
      const timer = setTimeout(() => {
        initializeSessionAndFetchHistory()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [propertyId, isVisible])

  const fetchInitialGreeting = async () => {
    if (!propertyId) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: "Please select a property to start the conversation.",
          sender: "bot",
          timestamp: new Date(),
        },
      ])
      return
    }

    setIsLoadingGreeting(true)
    try {
      const response = await axiosInstance.post(API_URL, {
        property_id: propertyId,
        intent: "initial_greeting",
        message: "",
      })

      if (response.data.success) {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            text: response.data.response || "Welcome! How can I assist you?",
            sender: "bot",
            timestamp: new Date(),
            action_suggestions: response.data.action_suggestions || [],
          },
        ])
        setRecommendations(response.data.recommendations || [])
      }
    } catch (error) {
      console.error("Error fetching initial greeting:", error)
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: error.response?.data?.message || "Failed to load greeting. Please try again.",
          sender: "bot",
          timestamp: new Date(),
          retry: true,
        },
      ])
    } finally {
      setIsLoadingGreeting(false)
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    if (isVisible) scrollToBottom()
  }, [messages, isVisible, recommendations])

  const handleSendMessage = async (intent = null, message = null, retryMessage = null) => {
    const messageToSend = retryMessage || (message !== null ? message.trim() : inputValue.trim())
    if (!messageToSend && !intent) return

    const newMessage = {
      id: Date.now(),
      text: messageToSend,
      sender: "user",
      timestamp: new Date(),
    }

    if (!retryMessage) {
      setMessages((prev) => [...prev, newMessage])
      if (message === null) setInputValue("")
    }
    setIsTyping(true)

    try {
      const payload = {
        message: messageToSend,
        property_id: propertyId,
        intent,
        preferences: {},
      }

      const response = await axiosInstance.post(API_URL, payload)
      if (response.data.success) {
        const botMessage = {
          id: Date.now(),
          text: response.data.response || "Sorry, I couldn't respond. Please try again.",
          sender: "bot",
          timestamp: new Date(),
          action_suggestions: response.data.action_suggestions || [],
        }

        const confirmAction = response.data.action_suggestions.find(
          (action) => action.type === "confirm_property" || action.type === "request_details",
        )
        if (confirmAction && confirmAction.property_id) {
          setPropertyId(confirmAction.property_id)
        }

        setMessages((prev) => [...prev, botMessage])
        setRecommendations(response.data.recommendations || [])
      } else {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            text: response.data.message || "An error occurred. Please try again.",
            sender: "bot",
            timestamp: new Date(),
            action_suggestions: response.data.action_suggestions || [],
            retry: true,
            retryMessage: messageToSend,
          },
        ])
      }
    } catch (error) {
      console.error("Error sending message:", error)
      const errorMessage =
        error.response?.data?.message || "An error occurred while sending your message. Please try again."
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: errorMessage,
          sender: "bot",
          timestamp: new Date(),
          retry: true,
          retryMessage: messageToSend,
        },
      ])
    } finally {
      setIsTyping(false)
    }
  }

  const handleRetry = (retryMessage) => {
    handleSendMessage(null, null, retryMessage)
  }

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleActionClick = (action) => {
    if (action.type === "confirm_property") {
      handleSendMessage("confirm_property", "Yes")
    } else if (action.type === "request_details") {
      handleSendMessage("request_details", "Yes")
    } else if (action.type === "provide_email" || action.type === "provide_contact") {
      setInputValue("")
      inputRef.current?.focus()
    } else if (action.endpoint) {
      window.location.href = action.endpoint
    }
  }

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: "Speech recognition is not supported on this device.",
          sender: "bot",
          timestamp: new Date(),
        },
      ])
      return
    }
    if (!isRecording) {
      recognitionRef.current.start()
      setIsRecording(true)
      inputRef.current?.focus()
    } else {
      recognitionRef.current.stop()
      setIsRecording(false)
    }
  }

  const toggleVisibility = () => {
    setIsVisible((prev) => !prev)
    setIsMinimized(false)
    if (!isVisible) {
      setTimeout(() => inputRef.current?.focus(), 300)
    }
  }

  const closeChat = () => {
    if (isMobile) {
      setIsVisible(false)
    } else {
      setIsMinimized(true)
      setIsVisible(false)
    }
  }

  const toggleMinimize = () => {
    setIsMinimized((prev) => !prev)
    if (!isMinimized) {
      setIsVisible(false)
    } else {
      setIsVisible(true)
      setTimeout(() => inputRef.current?.focus(), 300)
    }
  }

  const clearChatHistory = async () => {
    if (!propertyId) return
    try {
      setMessages([])
      setRecommendations([])
      fetchInitialGreeting()
    } catch (error) {
      console.error("Error clearing chat history:", error)
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: "Failed to clear chat history. Please try again.",
          sender: "bot",
          timestamp: new Date(),
        },
      ])
    }
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "24px"
      const maxHeight = isMobile ? 80 : isTablet ? 90 : 100
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, maxHeight)}px`
    }
  }, [inputValue, isMobile, isTablet])

  // Get responsive dimensions
  const getResponsiveDimensions = () => {
    if (isMobile) {
      return {
        containerClass: "inset-0",
        chatClass: "w-full h-screen flex flex-col", // Changed from h-full to h-screen
        headerClass: "flex-shrink-0 p-3 sm:p-4",
        chatAreaClass: "flex-1 overflow-y-auto p-3 sm:p-4", // Remove specific height, let flex-1 handle it
        inputAreaClass: "flex-shrink-0 p-3 sm:p-4",
        messageMaxWidth: "max-w-[90%] sm:max-w-[85%]",
        buttonSize: "p-2",
        iconSize: "w-5 h-5 sm:w-6 sm:w-6",
        avatarSize: "w-7 h-7 sm:w-8 sm:h-8",
        fontSize: "text-sm",
        spacing: "space-y-3",
      }
    } else if (isTablet) {
      return {
        containerClass: "top-4 right-4",
        chatClass: "w-[400px] h-[600px] md:w-[450px] md:h-[650px]",
        headerClass: "p-4",
        chatAreaClass: "h-[450px] md:h-[490px] overflow-y-auto p-4",
        inputAreaClass: "p-4",
        messageMaxWidth: "max-w-sm md:max-w-md",
        buttonSize: "p-2",
        iconSize: "w-5 h-5",
        avatarSize: "w-8 h-8",
        fontSize: "text-sm",
        spacing: "space-y-4",
      }
    } else {
      return {
        containerClass: "top-4 right-4",
        chatClass: "w-[450px] h-[650px] lg:w-[520px] lg:h-[700px] xl:w-[550px] xl:h-[720px]",
        headerClass: "p-4",
        chatAreaClass: "h-[490px] lg:h-[540px] xl:h-[560px] overflow-y-auto p-4",
        inputAreaClass: "p-4",
        messageMaxWidth: "max-w-sm lg:max-w-md xl:max-w-lg",
        buttonSize: "p-2",
        iconSize: "w-5 h-5",
        avatarSize: "w-8 h-8",
        fontSize: "text-sm",
        spacing: "space-y-4",
      }
    }
  }

  const dimensions = getResponsiveDimensions()

  return (
    <>
      {isMobile && isVisible && <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={closeChat} />}

      {/* Floating Chat Button */}
      {!isVisible && (
        <div className="fixed bottom-20 right-8 z-50 transition-all duration-500 ease-in-out">
          <div className="relative group">
            <button
              onClick={toggleVisibility}
              className="p-2 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 rounded-full shadow-xl transition-all duration-300 hover:scale-110 active:scale-95 hover:shadow-2xl"
              aria-label={isMinimized ? 'Reopen chat' : 'Open chat'}
              aria-describedby="chat-tooltip"
            >
              <ChatbotIcon size={40} />
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full animate-pulse"></div>
              <div className="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-20 group-active:opacity-30 transition-opacity duration-300"></div>
            </button>
            <div
              id="chat-tooltip"
              role="tooltip"
              className="absolute right-full mr-3 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap shadow-lg pointer-events-none"
            >
              {isMinimized ? 'Reopen JustOffPlan Chat' : 'Chat with JustOffPlan'}
              <div className="absolute left-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-8 border-transparent border-l-gray-800"></div>
            </div>
          </div>
        </div>
      )}

      {/* Main Chatbot Container */}
      <div
        className={`fixed z-60 transition-all duration-700 ease-in-out ${dimensions.containerClass} ${
          isVisible
            ? "translate-y-0 opacity-100 scale-100"
            : isMobile
              ? "translate-y-full opacity-0 scale-95"
              : "translate-x-full opacity-0 scale-90"
        }`}
        role="dialog"
        aria-label="Chatbot Interface"
        style={isMobile ? { height: "100vh", height: "100dvh" } : {}} // Add dynamic viewport height support
      >
        <div
          ref={chatContainerRef}
          className={`${dimensions.chatClass} bg-white border border-gray-200 ${
            isMobile ? "rounded-none" : "rounded-2xl"
          } shadow-2xl overflow-hidden backdrop-blur-xl relative`}
        >
          {/* Header */}
          <div className={`${dimensions.headerClass} border-b border-gray-200 bg-green-100`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div
                    className={`bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex justify-center items-center shadow-lg hover:shadow-xl transition-all duration-300 ${
                      isMobile ? "w-10 h-10" : "w-11 h-11"
                    }`}
                  >
                    <Bot className={`text-white ${isMobile ? "w-5 h-5" : "w-6 h-6"}`} />
                  </div>
                  <div
                    className={`absolute -top-1 -right-1 bg-green-400 rounded-full animate-pulse ${
                      isMobile ? "w-3 h-3" : "w-4 h-4"
                    }`}
                  ></div>
                </div>
                <div>
                  <h3 className={`font-bold text-gray-900 flex items-center gap-2 ${isMobile ? "text-lg" : "text-xl"}`}>
                    JustOffPlan
                    <span className={`font-normal text-teal-600 ${isMobile ? "text-xs" : "text-sm"}`}>Support</span>
                  </h3>
                  <p className={`text-gray-600 flex items-center ${isMobile ? "text-xs" : "text-sm"}`}>
                    <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                    {isTyping ? "Typing..." : "Online & Ready"}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={clearChatHistory}
                  className={`group relative bg-gray-100 hover:bg-red-50 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 ${dimensions.buttonSize}`}
                  aria-label="Clear chat history"
                >
                  <Trash2
                    className={`text-gray-600 group-hover:text-red-500 transition-colors duration-200 ${
                      isMobile ? "w-4 h-4" : "w-5 h-5"
                    }`}
                  />
                </button>
                <button
                  onClick={closeChat}
                  className={`group relative bg-gray-100 hover:bg-red-50 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 ${dimensions.buttonSize}`}
                  aria-label={isMobile ? "Close chat" : "Minimize chat"}
                >
                  {isMobile ? (
                    <X
                      className={`text-gray-600 group-hover:text-red-500 transition-colors duration-200 ${
                        isMobile ? "w-4 h-4" : "w-5 h-5"
                      }`}
                    />
                  ) : (
                    <ChevronRight
                      className={`text-gray-600 group-hover:text-teal-500 transition-all duration-200 group-hover:translate-x-1 ${
                        isMobile ? "w-4 h-4" : "w-5 h-5"
                      }`}
                    />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Chat Area */}
          <div className={`${dimensions.chatAreaClass} ${dimensions.spacing} bg-gray-50 relative`}>
            {messages.length === 0 && !isTyping && !isLoadingGreeting && !isLoadingHistory && (
              <div className={`text-center text-gray-500 ${dimensions.fontSize}`}>
                No messages yet. Start the conversation!
              </div>
            )}

            {(isLoadingGreeting || isLoadingHistory) && (
              <div className={`text-center text-gray-500 ${dimensions.fontSize} flex items-center justify-center`}>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                {isLoadingGreeting ? "Loading greeting..." : "Loading chat history..."}
              </div>
            )}

            {messages.map((message, index) => (
              <div
                key={message.id}
                className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"} animate-slide-in`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div
                  className={`${dimensions.messageMaxWidth} ${message.sender === "user" ? "order-2" : "order-1"} relative group`}
                >
                  <div
                    className={`flex items-end space-x-2 sm:space-x-3 ${message.sender === "user" ? "flex-row-reverse space-x-reverse" : ""}`}
                  >
                    <div
                      className={`${dimensions.avatarSize} rounded-full flex items-center justify-center shadow-sm ${
                        message.sender === "user"
                          ? "bg-gradient-to-br from-teal-500 to-teal-600"
                          : "bg-gradient-to-br from-gray-500 to-gray-600"
                      } transform hover:scale-105 transition-transform duration-200`}
                    >
                      {message.sender === "user" ? (
                        <User className={`text-white ${isMobile ? "w-3 h-3" : "w-4 h-4"}`} />
                      ) : (
                        <Bot className={`text-white ${isMobile ? "w-3 h-3" : "w-4 h-4"}`} />
                      )}
                    </div>
                    <div className="flex-1">
                      <div
                        className={`px-3 py-2 sm:px-4 sm:py-3 rounded-2xl shadow-sm border transition-all duration-200 hover:shadow-md relative ${
                          message.sender === "user"
                            ? "bg-teal-500 text-white border-teal-500"
                            : "bg-white text-gray-800 border-gray-200"
                        }`}
                      >
                        <p className={`${dimensions.fontSize} font-medium leading-relaxed break-words`}>
                          {message.text}
                        </p>
                        <p
                          className={`text-xs mt-1 sm:mt-2 text-right ${
                            message.sender === "user" ? "text-teal-100" : "text-gray-500"
                          }`}
                        >
                          {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>

                      {message.action_suggestions?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1 sm:gap-2">
                          {message.action_suggestions.map((action, idx) => (
                            <button
                              key={idx}
                              onClick={() => handleActionClick(action)}
                              className={`px-2 py-1 sm:px-3 sm:py-1 bg-teal-100 text-teal-800 rounded-lg hover:bg-teal-200 transition-colors duration-200 ${
                                isMobile ? "text-xs" : "text-sm"
                              }`}
                            >
                              {action.message}
                            </button>
                          ))}
                        </div>
                      )}

                      {message.retry && (
                        <div className="mt-2 flex justify-end">
                          <button
                            onClick={() => handleRetry(message.retryMessage)}
                            className={`px-2 py-1 sm:px-3 sm:py-1 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center ${
                              isMobile ? "text-xs" : "text-sm"
                            }`}
                          >
                            <RefreshCw className={`mr-1 sm:mr-2 ${isMobile ? "w-3 h-3" : "w-4 h-4"}`} />
                            Retry
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {recommendations.length > 0 && (
              <div className="mt-4">
                <h4 className={`font-semibold text-gray-700 mb-2 ${dimensions.fontSize}`}>Recommended Properties</h4>
                <div className="space-y-2">
                  {recommendations.map((property, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                    >
                      <h5 className={`font-medium text-gray-800 ${dimensions.fontSize}`}>{property.title}</h5>
                      <p className={`text-gray-600 ${isMobile ? "text-xs" : "text-xs"}`}>
                        Price: AED {Number.parseInt(property.price)} | {property.bedrooms} Beds | {property.bathrooms}{" "}
                        Baths | {property.area}
                      </p>
                      <p className={`text-gray-600 ${isMobile ? "text-xs" : "text-xs"}`}>
                        {property.location?.city}, {property.location?.emirate}
                      </p>
                      <button
                        onClick={() => {
                          setPropertyId(property.id)
                          handleSendMessage("view_property", `Tell me about ${property.title}`)
                        }}
                        className={`mt-2 px-2 py-1 sm:px-3 sm:py-1 bg-teal-100 text-teal-800 rounded-lg hover:bg-teal-200 ${
                          isMobile ? "text-xs" : "text-sm"
                        }`}
                      >
                        View Details
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isTyping && (
              <div className="flex justify-start animate-slide-in">
                <div className={`${dimensions.messageMaxWidth} flex items-end space-x-2 sm:space-x-3`}>
                  <div
                    className={`${dimensions.avatarSize} rounded-full bg-gradient-to-br from-gray-500 to-gray-600 flex items-center justify-center shadow-sm`}
                  >
                    <Bot className={`text-white ${isMobile ? "w-3 h-3" : "w-4 h-4"}`} />
                  </div>
                  <div className="bg-white px-3 py-2 sm:px-4 sm:py-3 rounded-2xl shadow-sm border border-gray-200">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce"></div>
                        <div
                          className="w-2 h-2 bg-teal-400 rounded-full animate-bounce"
                          style={{ animationDelay: "0.1s" }}
                        ></div>
                        <div
                          className="w-2 h-2 bg-teal-400 rounded-full animate-bounce"
                          style={{ animationDelay: "0.2s" }}
                        ></div>
                      </div>
                      <span className={`text-gray-600 font-medium ${dimensions.fontSize}`}>AI thinking...</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className={`${dimensions.inputAreaClass} border-t border-gray-200 bg-white`}>
            <div className="flex items-end space-x-2 sm:space-x-3 bg-gray-50 rounded-2xl p-2 sm:p-3 border border-gray-200 shadow-sm hover:bg-gray-100/50 transition-all duration-200">
              <MessageCircle className={`text-teal-500 mb-2 flex-shrink-0 ${isMobile ? "w-4 h-4" : "w-5 h-5"}`} />
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about properties..."
                className={`w-full bg-transparent text-gray-800 placeholder-gray-500 focus:outline-none resize-none font-medium leading-relaxed ${dimensions.fontSize}`}
                rows={1}
                style={{
                  minHeight: "24px",
                  maxHeight: isMobile ? "80px" : isTablet ? "90px" : "100px",
                }}
                aria-label="Chat input"
              />
              <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
                <button
                  onClick={toggleRecording}
                  className={`${dimensions.buttonSize} rounded-xl transition-all duration-200 ${
                    isRecording
                      ? "bg-red-500 animate-pulse shadow-lg shadow-red-500/30"
                      : "bg-gray-200 hover:bg-gray-300"
                  }`}
                  aria-label={isRecording ? "Stop recording" : "Start recording"}
                  disabled={!recognitionRef.current}
                >
                  <Mic
                    className={`${isRecording ? "text-white" : "text-gray-600"} ${isMobile ? "w-3 h-3" : "w-4 h-4"}`}
                  />
                </button>
                <button
                  onClick={() => handleSendMessage()}
                  disabled={!inputValue.trim()}
                  className={`bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl hover:from-teal-600 hover:to-teal-700 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-teal-500 disabled:hover:to-teal-600 transform hover:scale-105 active:scale-95 ${dimensions.buttonSize}`}
                  aria-label="Send message"
                >
                  <Send className={`text-white ${isMobile ? "w-3 h-3" : "w-4 h-4"}`} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-in {
          animation: slide-in 0.4s ease-out forwards;
        }
      `}</style>
    </>
  )
}

export default ChatBot